"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type BoulderingDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A bouldering gym wall under spotlights: three faceted plywood panels (a
 * slab, an overhang, an arête in shadow) with a teal volume as the focal
 * point, colour-coded problems with start tape and chalked-up holds, and a
 * thick crash pad below with a chalk bucket and a pair of shoes on the outer
 * thirds, clear of the bottom-centre participant pill.
 *
 * Holds sit in a px-unit SVG (nested <svg x="%">) so they keep their shape at
 * any card width while the panels stretch. Static art — no motion.
 */

/** Top of the crash pad, in px from the top of the scene. */
const PAD = 166;

type HoldShape = "jug" | "crimp" | "sloper" | "pinch";

const HOLD_PATHS: Record<HoldShape, string> = {
  jug: "M-7 -2 Q-6 -8 0 -8 Q6 -8 7 -2 Q7 4 0 4.5 Q-7 4 -7 -2 Z",
  crimp: "M-6 -1.5 L6 -3 L5.4 2 L-5.4 2.4 Z",
  sloper: "M-10 3 Q-9 -7 0 -7.5 Q10 -7 10 3 Q0 6.5 -10 3 Z",
  pinch: "M-3 -7 Q3 -8 4 0 Q3 7 -3 6.4 Q-5 0 -3 -7 Z",
};

type Hold = {
  x: string;
  y: number;
  shape: HoldShape;
  color: string;
  rotate?: number;
  chalk?: boolean;
  tape?: boolean;
};

const PINK = "#ff4f8b";
const YELLOW = "#ffd02a";
const BLUE = "#3b8cff";
const BLACK = "#2a2d33";
const GREEN = "#43c46a";

const HOLDS: Hold[] = [
  // Pink problem, climbing the slab
  { x: "10%", y: 146, shape: "jug", color: PINK, tape: true },
  { x: "20%", y: 120, shape: "pinch", color: PINK, rotate: 20, chalk: true },
  { x: "12%", y: 94, shape: "crimp", color: PINK, rotate: -10, chalk: true },
  { x: "26%", y: 70, shape: "sloper", color: PINK, chalk: true },
  { x: "18%", y: 46, shape: "jug", color: PINK, rotate: 15 },
  { x: "31%", y: 44, shape: "crimp", color: PINK, rotate: 8 },
  // Yellow problem, out the overhang and up the arête
  { x: "88%", y: 146, shape: "jug", color: YELLOW, tape: true },
  { x: "80%", y: 122, shape: "sloper", color: YELLOW, chalk: true },
  { x: "90%", y: 98, shape: "pinch", color: YELLOW, rotate: -25 },
  { x: "78%", y: 76, shape: "crimp", color: YELLOW, chalk: true },
  { x: "88%", y: 50, shape: "jug", color: YELLOW, rotate: -12 },
  { x: "74%", y: 44, shape: "sloper", color: YELLOW },
  // Blue problem through the volume
  { x: "40%", y: 140, shape: "crimp", color: BLUE, tape: true },
  { x: "62%", y: 126, shape: "pinch", color: BLUE, rotate: 30 },
  { x: "38%", y: 108, shape: "jug", color: BLUE, chalk: true },
  // Odds and ends
  { x: "68%", y: 150, shape: "crimp", color: BLACK, rotate: -18 },
  { x: "34%", y: 156, shape: "pinch", color: GREEN, rotate: -30 },
  { x: "66%", y: 98, shape: "crimp", color: GREEN, rotate: 12 },
  { x: "6%", y: 66, shape: "sloper", color: BLACK },
];

/** Bolt-on holes, every 20 units across the wall. */
const T_NUTS = Array.from({ length: 8 * 15 }, (_, i) => ({
  x: (i % 15) * 20 + 10,
  y: Math.floor(i / 15) * 20 + 10,
}));

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `bouldering-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={id("chalk")}>
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("slab")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e7e9ee" />
          <stop offset="1" stopColor="#b9bec8" />
        </linearGradient>
        <linearGradient id={id("overhang")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#8f98a8" />
          <stop offset="1" stopColor="#c3c9d3" />
        </linearGradient>
        <linearGradient id={id("arete")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#6c7586" />
          <stop offset="1" stopColor="#4c5465" />
        </linearGradient>
        <linearGradient id={id("bag")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#ff6a4a" />
          <stop offset="1" stopColor="#b8301a" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Three plywood facets + bolt grid — stretches with the card. */
function Wall({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <polygon
        points={`0,0 112,0 104,${PAD} 0,${PAD}`}
        fill={`url(#${id("slab")})`}
      />
      <polygon
        points={`112,0 206,0 214,${PAD} 104,${PAD}`}
        fill={`url(#${id("overhang")})`}
      />
      <polygon
        points={`206,0 300,0 300,${PAD} 214,${PAD}`}
        fill={`url(#${id("arete")})`}
      />
      {/* Kink where the overhang kicks out */}
      <polygon
        points="108,84 210,84 212,92 106,92"
        fill="#000"
        opacity="0.12"
      />
      {/* Panel seams */}
      <path
        d={`M112 0 L104 ${PAD} M206 0 L214 ${PAD} M0 84 H300`}
        stroke="#1d222b"
        strokeOpacity="0.35"
        strokeWidth="1"
      />
      <path
        d={`M113 0 L105 ${PAD}`}
        stroke="#fff"
        strokeOpacity="0.5"
        strokeWidth="0.8"
      />
      {/* T-nut grid */}
      {T_NUTS.map((nut) => (
        <rect
          key={`${nut.x}-${nut.y}`}
          x={nut.x}
          y={nut.y}
          width="1"
          height="1"
          fill="#1d222b"
          opacity="0.3"
        />
      ))}
      {/* Spotlight pools */}
      <ellipse cx="60" cy="10" rx="80" ry="60" fill="#fff8e6" opacity="0.18" />
      <ellipse cx="240" cy="10" rx="80" ry="60" fill="#fff8e6" opacity="0.12" />
    </svg>
  );
}

/** Holds, start tape, chalk, and the volume — px units, never stretched. */
function Holds({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute inset-x-0 top-0 w-full"
      style={{ height: PAD }}
      aria-hidden="true"
      focusable="false"
    >
      {/* The volume */}
      <svg x="54%" y="40" overflow="visible" aria-hidden="true">
        <polygon
          points="-36,6 40,0 6,48"
          fill="#000"
          opacity="0.25"
          transform="translate(4 6)"
        />
        <polygon points="-36,6 40,0 4,18" fill="#5fe0cf" />
        <polygon points="-36,6 4,18 6,48" fill="#25a596" />
        <polygon points="40,0 4,18 6,48" fill="#167a6f" />
        <path
          d="M-36 6 L40 0"
          stroke="#d2fff8"
          strokeOpacity="0.9"
          strokeWidth="1"
        />
        <path
          d="M4 18 L6 48"
          stroke="#0e554d"
          strokeOpacity="0.6"
          strokeWidth="0.8"
        />
        <path
          transform="translate(-10 16) rotate(-20)"
          d={HOLD_PATHS.crimp}
          fill={BLUE}
        />
        <path
          transform="translate(20 20) rotate(10)"
          d={HOLD_PATHS.jug}
          fill={BLUE}
        />
        <circle cx="20" cy="22" r="6" fill={`url(#${id("chalk")})`} />
      </svg>
      {HOLDS.map((hold) => (
        <svg
          key={`${hold.x}-${hold.y}`}
          x={hold.x}
          y={hold.y}
          overflow="visible"
          aria-hidden="true"
        >
          {hold.chalk ? <circle r="11" fill={`url(#${id("chalk")})`} /> : null}
          {hold.tape ? (
            <>
              <rect
                x="-9"
                y="8"
                width="7"
                height="2.4"
                fill="#fff"
                opacity="0.9"
                transform="rotate(-30 -5 9)"
              />
              <rect
                x="2"
                y="8"
                width="7"
                height="2.4"
                fill="#fff"
                opacity="0.9"
                transform="rotate(30 5 9)"
              />
            </>
          ) : null}
          <g transform={hold.rotate ? `rotate(${hold.rotate})` : undefined}>
            <path
              d={HOLD_PATHS[hold.shape]}
              fill="#000"
              opacity="0.3"
              transform="translate(1.6 2.4)"
            />
            <path d={HOLD_PATHS[hold.shape]} fill={hold.color} />
            <path
              d={HOLD_PATHS[hold.shape]}
              fill="none"
              stroke="#fff"
              strokeOpacity="0.45"
              strokeWidth="1"
              strokeDasharray="10 40"
            />
            <circle r="1.2" fill="#000" opacity="0.45" />
          </g>
        </svg>
      ))}
    </svg>
  );
}

function ChalkBucket({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute bottom-[18px] left-[4%]"
      width="58"
      height="38"
      viewBox="0 0 58 38"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="26" cy="35" rx="24" ry="3" fill="#000" opacity="0.4" />
      {/* Bucket bag */}
      <path d="M4 8 H34 L32 34 H6 Z" fill={`url(#${id("bag")})`} />
      <ellipse cx="19" cy="8" rx="15" ry="4" fill="#7a1e10" />
      <ellipse cx="19" cy="8.4" rx="12.6" ry="2.8" fill="#f4f4f2" />
      <rect x="4" y="18" width="30" height="3" fill="#2a2d33" opacity="0.6" />
      <rect
        x="6"
        y="10"
        width="2"
        height="22"
        rx="1"
        fill="#ffd0c4"
        opacity="0.6"
      />
      {/* Chalk puff */}
      <circle cx="22" cy="4" r="7" fill={`url(#${id("chalk")})`} />
      {/* Brush */}
      <rect
        x="38"
        y="26"
        width="18"
        height="4"
        rx="2"
        fill="#2f6fd6"
        transform="rotate(-14 47 28)"
      />
      <rect
        x="36"
        y="28"
        width="7"
        height="5"
        rx="1"
        fill="#e8d2a8"
        transform="rotate(-14 39 30)"
      />
    </svg>
  );
}

function ClimbingShoes() {
  const shoe =
    "M2 12 Q2 4 10 3 L22 2 Q30 2 34 8 Q36 12 32 14 L6 15 Q2 15 2 12 Z";
  return (
    <svg
      className="absolute right-[4%] bottom-[16px]"
      width="62"
      height="32"
      viewBox="0 0 62 32"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="32" cy="29" rx="28" ry="3" fill="#000" opacity="0.4" />
      <g transform="translate(22 10)">
        <path d={shoe} fill="#1f2228" />
        <path
          d="M2 12 Q2 14 6 15 L32 14 Q36 12 34 8"
          stroke="#ff7a2e"
          strokeWidth="2.6"
          fill="none"
        />
        <path
          d="M12 3.6 L20 3 M13 6.6 L21 6"
          stroke="#ff7a2e"
          strokeWidth="1.2"
        />
      </g>
      <g transform="translate(4 4) rotate(-8)">
        <path d={shoe} fill="#2a2e36" />
        <path
          d="M2 12 Q2 14 6 15 L32 14 Q36 12 34 8"
          stroke="#ff9a4a"
          strokeWidth="2.6"
          fill="none"
        />
        <path
          d="M12 3.6 L20 3 M13 6.6 L21 6"
          stroke="#ff9a4a"
          strokeWidth="1.2"
        />
        <path
          d="M10 3.4 L24 2.4"
          stroke="#fff"
          strokeOpacity="0.35"
          strokeWidth="0.8"
        />
      </g>
    </svg>
  );
}

export function BoulderingDisplayPreview({
  participants,
}: BoulderingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Bouldering"
      backgroundClassName="bg-[#5a6272]"
      participants={participants}
      scene={
        <>
          <SceneDefs id={id} />
          <Wall id={id} />
          <Holds id={id} />

          {/* Crash pad: thick, seamed, with a lit front lip */}
          <div
            className="absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,#3a4a63_0%,#2a3549_55%,#1b2232_100%)]"
            style={{ top: PAD }}
          >
            <div className="absolute inset-x-0 top-0 h-[3px] bg-[#7e93b8]/60" />
            <div className="absolute inset-x-0 top-[3px] h-[6px] bg-black/25" />
            <div className="absolute top-[3px] bottom-0 left-[33%] w-px bg-black/35" />
            <div className="absolute top-[3px] right-[33%] bottom-0 w-px bg-black/35" />
            <div className="absolute inset-x-0 top-[26px] h-px bg-black/25" />
          </div>
          {/* Chalk dust on the pad under the wall */}
          <div
            className="absolute inset-x-[8%] h-[14px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.18),transparent)]"
            style={{ top: PAD - 2 }}
          />

          <ChalkBucket id={id} />
          <ClimbingShoes />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_50%_25%,transparent_45%,rgba(14,18,28,0.5)_100%)]" />
      }
    />
  );
}
